import {
  APIErrorCodes,
  ManyIncludedUserResourcePermissionResource,
  SingleIncludedUserResourcePermissionResource,
} from 'api-user-client'
import axios, { AxiosError } from 'axios'
import { uniq } from 'ramda'

import {
  AdditionalRequestConfig,
  request,
} from '~shared-components/api/request'
import { UserFilter } from '~shared-components/filters/users.filters'
import {
  dateFilterToAPIQuery,
  FilterMap,
  mapFiltersToAPIQuery,
} from '~shared-components/filters/utils'
import {
  ApplicationUser,
  ExpandedUserResource,
  transformApiUserToUser,
  transformApplicationUserToApiUser,
  TransformedUser,
} from '~shared-components/transforms/user.transforms'
import { SortingType } from '~shared-components/translations/sorting'
import { deserialize } from '~shared-components/utils/deserialize'

const userApiHref = '/api-user/users'

export class UserWithEmailAddressAlreadyExistsError extends Error {
  public email: string

  public constructor(email: string) {
    super(`User with email address ${email} already exists`)
    this.email = email
  }
}

export class UnexpectedError extends Error {
  public error: Error

  public constructor(error: Error) {
    super(error.message)
    this.error = error
  }
}

export class PermissionsOutsideOfSalesGroupError extends Error {
  public constructor(error: AxiosError) {
    super(error.response?.data.details)
  }
}

export type CreateUserError =
  | UserWithEmailAddressAlreadyExistsError
  | UnexpectedError
  | PermissionsOutsideOfSalesGroupError

export interface UpdateUserParameters {
  customerContextIds?: string[]
  salesGroupIds?: string[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isError = (error: any): error is Error => error?.name !== undefined
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isAxiosError = (error: any): error is AxiosError => error?.isAxiosError
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformSavingError = (error: any, user: ApplicationUser) => {
  if (isAxiosError(error)) {
    const { data, status } = error.response ?? {}

    if (
      status === 409 &&
      ['E_RESOURCE_EXISTS', 'E_EMAIL_EXISTS_AUTH0'].includes(data.code)
    ) {
      return new UserWithEmailAddressAlreadyExistsError(user.email)
    }

    if (status === 403 && data.code === APIErrorCodes.OutsideSalesGroup) {
      return new PermissionsOutsideOfSalesGroupError(error as AxiosError)
    }
  }

  return new UnexpectedError(isError(error) ? error : new Error(String(error)))
}

export const createUser = async (
  user: ApplicationUser,
  config: AdditionalRequestConfig = {},
): Promise<TransformedUser> => {
  try {
    const {
      data,
    } = await axios.post<SingleIncludedUserResourcePermissionResource>(
      `${config.baseUrl || ''}${userApiHref}`,
      transformApplicationUserToApiUser(user),
      {
        headers: config.headers,
      },
    )

    return transformApiUserToUser(deserialize<ExpandedUserResource>(data))
  } catch (error) {
    throw transformSavingError(error, user)
  }
}

export const getUser = async (
  userId: string,
  salesGroupIds?: string[],
  customerContextIds?: string[],
) => {
  const {
    data,
  } = await axios.request<SingleIncludedUserResourcePermissionResource>({
    params: {
      'filter[permissions.customerContextId.in]': customerContextIds?.join(','),
      'filter[permissions.salesGroupId.in]': salesGroupIds?.join(','),
      include: 'permissions',
    },
    url: userId === 'me' ? '/api-user/me' : `${userApiHref}/${userId}`,
  })
  return transformApiUserToUser(deserialize<ExpandedUserResource>(data))
}

export type SaveUserError = CreateUserError

export const saveUser = async (
  user: ApplicationUser,
  params: UpdateUserParameters,
  config: AdditionalRequestConfig = {},
): Promise<TransformedUser> => {
  try {
    const {
      data,
    } = await axios.patch<SingleIncludedUserResourcePermissionResource>(
      `${config.baseUrl || ''}${userApiHref}/${user.id}`,
      transformApplicationUserToApiUser(user),
      {
        headers: config.headers,
        params: {
          'filter[permissions.customerContextId.in]': params.customerContextIds?.join(
            ',',
          ),
          'filter[permissions.salesGroupId.in]': params.salesGroupIds?.join(
            ',',
          ),
        },
      },
    )

    return transformApiUserToUser(deserialize<ExpandedUserResource>(data))
  } catch (error) {
    throw transformSavingError(error, user)
  }
}

export const deleteUser = async (userId: string) => {
  await axios.delete(`${userApiHref}/${userId}`)
}

const transformSort = (sort: string | undefined) => {
  if (sort === undefined) return undefined

  const transform: Partial<Record<SortingType, string>> = {
    '-customerName': '-permissions.customerContext.customerName',
    '-username': '-name',
    '-usertype': '-type',
    customerName: 'permissions.customerContext.customerName',
    username: 'name',
    usertype: 'type',
  }

  return transform[sort as SortingType] ?? sort
}

/**
 *  Any transforms that need to be done to the filter query
 *  before it gets passed along to the API should go here
 */
const formatFilters = ({
  createdDateGreaterEqual,
  createdDateLessEqual,
  lastLoginDateGreaterEqual,
  lastLoginDateLessEqual,
  sort,
  ...rest
}: FilterMap<typeof UserFilter>) => ({
  ...rest,
  createdDateGreaterEqual: dateFilterToAPIQuery(createdDateGreaterEqual),
  createdDateLessEqual: dateFilterToAPIQuery(createdDateLessEqual),
  lastLoginDateGreaterEqual: dateFilterToAPIQuery(lastLoginDateGreaterEqual),
  lastLoginDateLessEqual: dateFilterToAPIQuery(lastLoginDateLessEqual),
  sort: transformSort(sort),
})

interface FetchUsersParams {
  customerContextIds?: string[]
  filters: FilterMap<typeof UserFilter>
  pageNumber: number
  pageSize: number
  salesGroupIds?: string[]
}

export const fetchUsers = async (
  {
    customerContextIds,
    filters,
    pageNumber,
    pageSize,
    salesGroupIds,
  }: FetchUsersParams,
  config: AdditionalRequestConfig = {},
) => {
  try {
    const response = await request<ManyIncludedUserResourcePermissionResource>({
      headers: {
        'Content-Type': 'application/json',
        ...(config.headers || {}),
      },
      method: 'GET',
      params: {
        'filter[permissions.customerContextId.in]':
          customerContextIds && uniq(customerContextIds).join(','),
        'filter[permissions.salesGroupId.in]':
          salesGroupIds && uniq(salesGroupIds).join(','),
        include: 'permissions',
        'page[number]': pageNumber,
        'page[size]': pageSize,
        ...mapFiltersToAPIQuery(formatFilters(filters), UserFilter),
      },
      url: `${config.baseUrl || ''}${userApiHref}`,
    })

    const deserializedData = deserialize<ExpandedUserResource[]>(response?.data)

    if (response) {
      return {
        total: response?.data?.meta?.count | 0,
        users: deserializedData.map(user =>
          transformApiUserToUser(user),
        ) as TransformedUser[],
      }
    } else {
      return {
        error: true,
        total: 0,
        users: [] as TransformedUser[],
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)

    return {
      error: true,
      total: 0,
      users: [] as TransformedUser[],
    }
  }
}
